// src/components/IdealCandidateView/IdealCandidateView.js

import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Box, Paper, Tab, Dialog, DialogContent, DialogTitle, Button, Typography, Grid, Stack, Menu, MenuItem, Checkbox, Slider, alpha, useTheme, DialogActions } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { useTour } from '../../shared/TourContext';
import ModalContext from '../../shared/ModalContext/ModalContext';
import SortIcon from '@mui/icons-material/SortByAlpha';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

// Local Imports
import IdealCandidateHeader from './components/IdealCandidateHeader';
import PotentialCandidatesTable from './components/PotentialCandidatesTable';
import BoardView from './components/BoardView/BoardView';
import ComparisonActionBar from './components/ComparisonView/ComparisonActionBar';
import CreateIdealCandidateForm from "../CreateIdealCandidateForm/CreateIdealCandidateForm";
import PotentialCandidateView from "../PotentialCandidateView/PotentialCandidateView";
import PotentialCandidateForm from "../PotentialCandidateForm";
import { useCandidateFilters } from '../../hooks/useCandidateFilters';
import IdealCandidateReport from './components/IdealCandidateReport';

const initialBoardState = {
  candidates: {},
  columns: {
    'pending': { id: 'pending', title: 'Pending', candidateIds: [] },
    'approved': { id: 'approved', title: 'Approved', candidateIds: [] },
    'declined': { id: 'declined', title: 'Declined', candidateIds: [] },
  },
  columnOrder: ['pending', 'approved', 'declined'],
};

export const ArchiveConfirmationDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={() => onClose(false)} PaperProps={{ sx: { borderRadius: 4 } }}>
    <DialogTitle>Archive Profile?</DialogTitle>
    <DialogContent><Typography color="textSecondary" variant="body2">This will hide the profile and its candidates from the main view. You can restore it later.</Typography></DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={() => onClose(false)} sx={{ textTransform: "none", borderRadius: "20px" }}>Cancel</Button>
      <Button color="error" onClick={() => onClose(true)} sx={{ textTransform: "none", borderRadius: "20px" }}>Archive</Button>
    </DialogActions>
  </Dialog>
);

export const UnarchiveConfirmationDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={() => onClose(false)} PaperProps={{ sx: { borderRadius: 4 } }}>
    <DialogTitle>Unarchive Profile?</DialogTitle>
    <DialogContent><Typography color="textSecondary" variant="body2">This will make the profile and its candidates visible again.</Typography></DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={() => onClose(false)} sx={{ textTransform: "none", borderRadius: "20px" }}>Cancel</Button>
      <Button color="primary" onClick={() => onClose(true)} sx={{ textTransform: "none", borderRadius: "20px" }}>Unarchive</Button>
    </DialogActions>
  </Dialog>
);


export const CreatePotentialCandidateDialog = ({ open, idealCandidate, onSubmit, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
    <DialogTitle>Create Potential Candidate</DialogTitle>
    <DialogContent>
      <PotentialCandidateForm showSuccess={false} includeHeader={false} idealCandidate={idealCandidate} onSubmit={onSubmit} />
    </DialogContent>
  </Dialog>
);

export const ViewCandidateDialog = ({ open, idealCandidate, potentialCandidate, onClose }) => (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent sx={{ p: 0 }}>
            <PotentialCandidateView 
                idealCandidate={idealCandidate} 
                potentialCandidate={potentialCandidate} 
                onClose={onClose} 
            />
        </DialogContent>
    </Dialog>
);

export default function IdealCandidateView({
  idealCandidate,
  potentialCandidates,
  onArchive,
  onUnarchive,
  onShare,
  onCreatePotentialCandidate,
  onUpdateCandidateStatus,
  initialStatuses,
  initialSkillScore,
  initialBehaviorScore,
}) {
  const theme = useTheme(); 
  const navigateInternal = useNavigate();
  const { openModal, closeModal } = useContext(ModalContext);

  const [currentTab, setCurrentTab] = useState("potential-candidates");
  const [viewMode, setViewMode] = useState('table');
  const [boardData, setBoardData] = useState(initialBoardState);
  const [selectedIds, setSelectedIds] = useState([]);
  
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [skillAnchorEl, setSkillAnchorEl] = useState(null);
  const [behaviorAnchorEl, setBehaviorAnchorEl] = useState(null);

  const {
    filters,
    setFilter,
    toggleStatus,
    resetFilters,
    filteredCandidates,
  } = useCandidateFilters(potentialCandidates, {
    statuses: initialStatuses,
    skillScore: initialSkillScore,
    behaviorScore: initialBehaviorScore,
  });
  const statusLabel = (s) => ({ approved: "Approved", pending: "Pending", declined: "Declined" }[s] || "Unknown");

  const handleOpenScheduler = (candidate) => {
    openModal('SCHEDULE_INTERVIEW', { candidate, jobTitle: idealCandidate?.title });
  };

  const handleViewCandidate = (email) => {
      const candidateToView = potentialCandidates.find(pc => pc.candidate.email === email);
      if (candidateToView) {
          openModal('VIEW_CANDIDATE_DETAILS', { idealCandidate, potentialCandidate: candidateToView });
      }
  };

  useEffect(() => {
    if (filteredCandidates) {
      const newBoardData = JSON.parse(JSON.stringify(initialBoardState));
      filteredCandidates.forEach(pc => {
        newBoardData.candidates[pc.id] = pc;
        if (newBoardData.columns[pc.status]) {
          newBoardData.columns[pc.status].candidateIds.push(pc.id);
        }
      });
      setBoardData(newBoardData);
    }
  }, [filteredCandidates]);

  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) return;
    
    const newStatus = destination.droppableId;
    onUpdateCandidateStatus(draggableId, newStatus);
  };

  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedIds.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) newSelected = [...selectedIds, id];
    else newSelected = selectedIds.filter(selectedId => selectedId !== id);
    setSelectedIds(newSelected);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelecteds = filteredCandidates.map((n) => n.id);
      setSelectedIds(newSelecteds);
      return;
    }
    setSelectedIds([]);
  };

  const selectedCandidatesForModal = useMemo(() => {
    return potentialCandidates.filter(pc => selectedIds.includes(pc.id));
  }, [selectedIds, potentialCandidates]);

  const handleArchiveClick = async () => {
    const confirmed = await openModal('ARCHIVE_CONFIRMATION');
    if (confirmed) {
      await onArchive();
    } else {
      console.log("User cancelled archive operation.");
    }
  };

  const handleUnarchiveClick = async () => {
    const confirmed = await openModal('UNARCHIVE_CONFIRMATION');
    if (confirmed) {
      await onUnarchive();
      navigateInternal('/'); 
    } else {
      console.log("User cancelled unarchive operation.");
    }
  };

  const handleCreateClick = (formValues) => { 
      onCreatePotentialCandidate({ ...formValues, criteria: idealCandidate?.criteria || {} }); 
      closeModal(); 
  };
  
  const { setSteps } = useTour();
  useEffect(() => { setSteps([]); return () => setSteps([]); }, [setSteps]);
  
  const filterButtonSx = {
    textTransform: 'none', minWidth: { xs: 120, sm: 140 }, justifyContent: 'space-between', borderColor: theme.palette.divider,
    color: theme.palette.text.primary, backgroundColor: theme.palette.background.paper, fontSize: '0.875rem', fontWeight: 400,
    '& .MuiButton-endIcon': { ml: 'auto' }, '&:hover': { borderColor: theme.palette.grey[500], backgroundColor: alpha(theme.palette.action.hover, 0.08) }
  };
  const menuPaperPropsSx = {
    elevation: 3, sx: { borderRadius: '8px', mt: 0.75, minWidth: '200px', boxShadow: theme.shadows[3] }
  };

  return (
    <Paper sx={{ borderRadius: 4, p: {xs: 2, md: 4}, bgcolor: 'background.paper' }}>
      <IdealCandidateHeader
        idealCandidate={idealCandidate}
        onPassiveSearch={() => navigateInternal("/passive-search", { state: { jobDescription: idealCandidate?.description } })}
        onArchive={handleArchiveClick}
        onUnarchive={handleUnarchiveClick}
        onShare={onShare}
        onAddCandidate={() => openModal('CREATE_POTENTIAL_CANDIDATE', { idealCandidate: idealCandidate, onSubmit: handleCreateClick })}
        viewMode={viewMode}
        onSetViewMode={setViewMode}
      />
      
      <Paper data-tour="filters" variant="outlined" sx={{p:2.5, borderRadius: '12px', borderColor: 'divider', bgcolor: 'action.hover', mb: 3}}>
        <Grid container spacing={{xs: 1.5, sm: 2}} alignItems="flex-end">
          <Grid item xs={12} sm={6} md="auto"><Typography variant="caption" display="block" color="text.secondary" sx={{mb:0.5, ml:0.5}}>Status</Typography><Button variant="outlined" size="small" onClick={(e) => setStatusAnchorEl(e.currentTarget)} endIcon={<ArrowDropDownIcon />} sx={filterButtonSx}>{filters.statuses.length === 3 || filters.statuses.length === 0 ? "All Statuses" : filters.statuses.map(s => statusLabel(s)).join(', ')}</Button><Menu anchorEl={statusAnchorEl} open={Boolean(statusAnchorEl)} onClose={() => setStatusAnchorEl(null)} PaperProps={menuPaperPropsSx}>{["approved", "pending", "declined"].map((statusKey) => (<MenuItem key={statusKey} onClick={() => toggleStatus(statusKey)} sx={{fontSize: '0.875rem'}}><Checkbox size="small" checked={filters.statuses.includes(statusKey)} sx={{mr:0.5, p:0.5}}/>{statusLabel(statusKey)}</MenuItem>))}</Menu></Grid>
          <Grid item xs={12} sm={6} md="auto"><Typography variant="caption" display="block" color="text.secondary" sx={{mb:0.5, ml:0.5}}> Skill Score</Typography><Button variant="outlined" size="small" onClick={(e) => setSkillAnchorEl(e.currentTarget)} endIcon={<ArrowDropDownIcon />} sx={filterButtonSx}>{filters.skillScore}%</Button><Menu anchorEl={skillAnchorEl} open={Boolean(skillAnchorEl)} onClose={() => setSkillAnchorEl(null)} PaperProps={menuPaperPropsSx} MenuListProps={{ sx: { py: 0 } }}><Box width={{xs: 240, sm: 275}} px={2.5} py={2}><Slider marks={[{value: 0, label: '0%'}, {value: 100, label: '100%'}]} value={filters.skillScore} onChange={(e, val) => setFilter('skillScore', val)} step={5} min={0} max={100} valueLabelDisplay="auto" size="small" /></Box></Menu></Grid>
          <Grid item xs={12} sm={6} md="auto"><Typography variant="caption" display="block" color="text.secondary" sx={{mb:0.5, ml:0.5}}> Behavior Score</Typography><Button variant="outlined" size="small" onClick={(e) => setBehaviorAnchorEl(e.currentTarget)} endIcon={<ArrowDropDownIcon />} sx={filterButtonSx}>{filters.behaviorScore}%</Button><Menu anchorEl={behaviorAnchorEl} open={Boolean(behaviorAnchorEl)} onClose={() => setBehaviorAnchorEl(null)} PaperProps={menuPaperPropsSx} MenuListProps={{ sx: { py: 0 } }}><Box width={{xs: 240, sm: 275}} px={2.5} py={2}><Slider marks={[{value: 0, label: '0%'}, {value: 100, label: '100%'}]} value={filters.behaviorScore} onChange={(e, val) => setFilter('behaviorScore', val)} step={5} min={0} max={100} valueLabelDisplay="auto" size="small" /></Box></Menu></Grid>
          <Grid item xs={12} sm={6} md="auto"><Typography variant="caption" display="block" color="text.secondary" sx={{mb:0.5, ml:0.5}}>Sort by Date</Typography><Button variant="outlined" size="small" onClick={() => setFilter('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc')} startIcon={<SortIcon sx={{ transform: filters.sortOrder === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s', fontSize: '1.125rem' }} />} sx={{...filterButtonSx, minWidth: {xs: 120, sm: 150}}}>{filters.sortOrder === 'asc' ? 'Oldest First' : 'Newest First'}</Button></Grid>
          <Grid item xs /><Grid item xs={12} md="auto"><Stack direction={{xs: "column", sm:"row"}} spacing={1.5} alignItems="center" sx={{width: '100%', justifyContent: {xs:'stretch', sm:'flex-end'}, mt:{xs:2, sm:0, md: 'auto' } }}><Button size="small" variant="text" onClick={resetFilters} sx={{ textTransform: 'none', borderRadius: '18px', px: 3, py: 0.75 }}>RESET</Button></Stack></Grid>
        </Grid>
      </Paper>

      <TabContext value={currentTab}>
        <TabList onChange={(_, v) => setCurrentTab(v)} indicatorColor="primary" textColor="primary" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab data-tour="potential-candidates-tab" label="Potential Candidates" value="potential-candidates" />
          <Tab data-tour="details-tab" label="Details" value="details" />
          <Tab label="Report" value="report" /> 
        </TabList>
        <TabPanel value="potential-candidates" sx={{ p: 0 }}>
            {viewMode === 'table' ? (
                <PotentialCandidatesTable
                    candidates={filteredCandidates}
                    onCandidateClick={handleViewCandidate}
                    selectedIds={selectedIds}
                    onSelectOne={handleSelectOne}
                    onSelectAll={handleSelectAll}
                    onSchedule={handleOpenScheduler}
                />
            ) : (
                <BoardView boardData={boardData} onDragEnd={handleDragEnd} />
            )}
        </TabPanel>
        <TabPanel value="details"><CreateIdealCandidateForm initialData={idealCandidate} viewOnly={true} /></TabPanel>
        <TabPanel value="report">
            <IdealCandidateReport idealCandidate={idealCandidate} />
      </TabPanel>
      </TabContext>

      <ComparisonActionBar 
        selectedCount={selectedIds.length}
        onCompare={() => openModal('CANDIDATE_COMPARISON', { candidates: selectedCandidatesForModal, idealCandidate: idealCandidate })}
        onClear={() => setSelectedIds([])}
      />
    </Paper>
  );
}
